import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { STORAGE_KEYS } from '../constants';
import { getFromStorage, setToStorage, removeFromStorage } from '../utils/helpers';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check authentication status on mount
  useEffect(() => {
    const token = sessionStorage.getItem('access_token') || localStorage.getItem(STORAGE_KEYS.TOKEN);
    const storedUser = getFromStorage(STORAGE_KEYS.USER);
    
    if (token && storedUser) {
      setUser(storedUser);
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  // Login function - Instagram-like (username + password)
  const login = useCallback(async (username, password, site = null) => {
    try {
setLoading(true);
      const response = await apiService.login(username, password, site);
if (response.success) {
// Set user state first, then authentication state
        setUser(response.user);
        setIsAuthenticated(true);
        setToStorage(STORAGE_KEYS.USER, response.user);
        // Small delay to ensure state is properly set
        await new Promise(resolve => setTimeout(resolve, 100));
return { success: true };
      } else {
return { success: false, error: response.error };
      }
    } catch (error) {
      console.error('useAuth: Login error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    apiService.logout();
    setUser(null);
    setIsAuthenticated(false);
    removeFromStorage(STORAGE_KEYS.USER);
    navigate('/');
  }, [navigate]);

  // Check if user has specific role
  const hasRole = useCallback((role) => {
    return user?.role === role;
  }, [user]);

  // Check if user has access to specific site
  const hasSiteAccess = useCallback((siteId) => {
    return user?.site_id === siteId || user?.role === 'admin';
  }, [user]);

  // Forgot password function
  const forgotPassword = useCallback(async (email) => {
    try {
      setLoading(true);
      const response = await apiService.forgotPassword(email);
return { success: true, message: response.message, resetToken: response.resetToken, resetUrl: response.resetUrl };
    } catch (error) {
      console.error('useAuth: Forgot password error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset password function
  const resetPassword = useCallback(async (token, password, passwordConfirm, siteId = null) => {
    try {
      setLoading(true);
      const response = await apiService.resetPassword(token, password, passwordConfirm || password, siteId);
if (response.success) {
        // Auto-login after successful password reset
        setUser(response.user);
        setIsAuthenticated(true);
        setToStorage(STORAGE_KEYS.USER, response.user);
        return { success: true, message: response.message, user: response.user };
      }
      
      return { success: false, error: response.error };
    } catch (error) {
      console.error('useAuth: Reset password error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    hasRole,
    hasSiteAccess,
    forgotPassword,
    resetPassword,
  };
};

